import { Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import html2canvas from 'html2canvas';
import { useEffect, useState } from 'react';
import { DrawerId, SideDrawer, SideDrawerHeader, useSideDrawer } from 'src/components/containers/SideDrawer';
import { AgButton, AgText } from 'src/components/ui';
import { notify } from 'src/lib/notify';
import {
  type FeedbackType,
  feedbackSendFailureMessage,
  useSubmitCustomerFeedback,
} from './hooks/useSubmitCustomerFeedback';

type FeedbackContext = { pagePath: string; recordRef?: string; label: string; screenshot?: Blob; previewUrl?: string };

export function FeedbackDrawer() {
  const { closeDrawer, payload } = useSideDrawer();
  const context = payload as FeedbackContext | undefined;
  const [type, setType] = useState<FeedbackType>('IDEA');
  const [body, setBody] = useState('');
  const [screenshot, setScreenshot] = useState<Blob>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const { submit, loading } = useSubmitCustomerFeedback();

  useEffect(() => {
    setScreenshot(context?.screenshot);
    setPreviewUrl(context?.previewUrl);
  }, [context]);

  const send = async () => {
    if (!context || body.trim() === '') return;
    try {
      await submit(
        {
          category: type,
          body: body.trim(),
          pageUrl: new URL(context.pagePath, window.location.origin).toString(),
          recordRef: context.recordRef,
        },
        screenshot
      );
      setBody('');
      closeDrawer();
    } catch (error) {
      // The drawer stays open with the text so the user can resend the same draft.
      notify.error(feedbackSendFailureMessage(error));
    }
  };

  return (
    <SideDrawer drawerId={DrawerId.CUSTOMER_FEEDBACK} paperSx={{ width: 440 }}>
      <SideDrawerHeader title="Feedback" />
      <div className="flex flex-1 flex-col gap-5 pt-2">
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={type}
          onChange={(_, next: FeedbackType | null) => next && setType(next)}
          aria-label="Feedback type"
        >
          <ToggleButton value="IDEA">Idea</ToggleButton>
          <ToggleButton value="PROBLEM">Problem</ToggleButton>
          <ToggleButton value="QUESTION">Question</ToggleButton>
        </ToggleButtonGroup>

        <div>
          <AgText size="13" color="secondary" className="mb-2">
            From
          </AgText>
          <Chip label={context?.label ?? 'Current page'} size="small" />
        </div>

        <label className="flex flex-col gap-2 text-14 font-medium">
          Tell us what is on your mind
          <textarea
            className="min-h-36 resize-y rounded-md border border-neutral-5 p-3 font-normal outline-none focus:border-brand-6 focus:ring-2 focus:ring-ring-primary"
            placeholder="What should we know?"
            maxLength={10_000}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </label>

        {previewUrl && screenshot && (
          <div className="rounded-md border border-neutral-5 p-2">
            <div className="mb-2 flex items-center justify-between">
              <AgText size="13" weight="semibold">
                Current page screenshot
              </AgText>
              <AgButton variant="ghost" size="sm" onPress={() => setScreenshot(undefined)}>
                Remove
              </AgButton>
            </div>
            <img
              className="max-h-44 w-full rounded object-cover object-top"
              src={previewUrl}
              alt="Current page screenshot"
            />
          </div>
        )}

        <div className="mt-auto pt-4">
          <AgButton
            variant="primary"
            className="w-full justify-center"
            isDisabled={body.trim() === '' || loading}
            onPress={send}
          >
            {loading ? 'Sending…' : 'Send to the Agriful team'}
          </AgButton>
        </div>
      </div>
    </SideDrawer>
  );
}

export async function captureFeedbackContext(pagePath: string): Promise<FeedbackContext> {
  const heading = document.querySelector<HTMLElement>('#page-title, main h1, main [data-page-title]')?.innerText.trim();
  const recordRef = heading || undefined;
  const label = recordRef ? `${recordRef} · ${pagePath}` : pagePath;
  try {
    const canvas = await html2canvas(document.querySelector<HTMLElement>('main') ?? document.body, {
      scale: Math.min(window.devicePixelRatio, 1),
      useCORS: true,
    });
    const screenshot = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.72));
    if (screenshot) return { pagePath, recordRef, label, screenshot, previewUrl: URL.createObjectURL(screenshot) };
  } catch {
    // Feedback remains useful if a cross-origin image prevents page capture.
  }
  return { pagePath, recordRef, label };
}
